import type { DeltaCell, DeltaDirection, DeltaMetric, Overview, OverviewDelta } from "@/lib/analytics/types";

/**
 * Computes (current - previous) / previous.
 * Returns 0 when previous is 0 (undefined base — no opinion).
 */
export function percentDelta(current: number, previous: number): number {
  if (previous === 0) return 0;
  return (current - previous) / previous;
}

/**
 * Maps a metric + delta to a semantic direction.
 *
 * up-is-good  : successRate, firstPassSuccessRate
 * down-is-good: p50/p95 duration, p50/p95 queue, rerunRate, estimatedBillableMinutes
 * neutral     : totalRuns, activeWorkflows
 */
// Module-scope constants — allocated once, O(1) Set lookups per call.
const UP_IS_GOOD: ReadonlySet<DeltaMetric> = new Set([
  "successRate",
  "firstPassSuccessRate",
]);
const DOWN_IS_GOOD: ReadonlySet<DeltaMetric> = new Set([
  "p50DurationMs",
  "p95DurationMs",
  "p50QueueTimeMs",
  "p95QueueTimeMs",
  "rerunRate",
  "estimatedBillableMinutes",
]);

export function deltaDirection(
  metric: DeltaMetric,
  current: number,
  previous: number,
): DeltaDirection {
  const delta = percentDelta(current, previous);
  if (delta === 0) return "neutral";
  if (UP_IS_GOOD.has(metric)) return delta > 0 ? "good" : "bad";
  if (DOWN_IS_GOOD.has(metric)) return delta < 0 ? "good" : "bad";
  // neutral metrics: totalRuns, activeWorkflows
  return "neutral";
}

/** Builds a full OverviewDelta from two Overview snapshots. */
export function computeOverviewDelta(
  current: Overview,
  previous: Overview,
): OverviewDelta {
  function cell(metric: DeltaMetric): DeltaCell {
    const cur = current[metric];
    const prev = previous[metric];
    return {
      value: percentDelta(cur, prev),
      direction: deltaDirection(metric, cur, prev),
    };
  }

  return {
    totalRuns: cell("totalRuns"),
    successRate: cell("successRate"),
    firstPassSuccessRate: cell("firstPassSuccessRate"),
    p50DurationMs: cell("p50DurationMs"),
    p95DurationMs: cell("p95DurationMs"),
    p50QueueTimeMs: cell("p50QueueTimeMs"),
    p95QueueTimeMs: cell("p95QueueTimeMs"),
    estimatedBillableMinutes: cell("estimatedBillableMinutes"),
    activeWorkflows: cell("activeWorkflows"),
    rerunRate: cell("rerunRate"),
  };
}
